import { useTranslation } from "react-i18next";
import { Spinner } from "@boligportal/bricks-ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { propertyOverviewQueries } from "~/api/property-overview/queries";
import { FallbackEmpty } from "~/components/FallbackEmpty";
import { Notice } from "~/components/ui/Notice";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { PropertyRouteError } from "./-components/PropertyRouteError";
import {
  bfeNumberSchema,
  propertySearchSchema,
  propertySelectionSchema,
  type PropertySelection,
} from "./-propertyTabs";

export const Route = createFileRoute("/(restricted)/ejendomme/$streetNumberId")(
  {
    component: RouteComponent,
    validateSearch: propertySearchSchema,
    params: {
      parse: (params) =>
        propertySelectionSchema.pick({ streetNumberId: true }).parse(params),
    },
    errorComponent: PropertyRouteError,
    loader: async ({ context: { i18n } }) => {
      await i18n.loadNamespaces(["properties", "restrictions"]);

      const title = i18n.t("properties:meta.title");

      return { meta: { title } };
    },
    head: async (context) => {
      return {
        meta: [
          {
            title: context.loaderData?.meta.title,
          },
        ],
      };
    },
  },
);

function RouteComponent() {
  const { streetNumberId } = Route.useParams();
  const { flags } = useFeatureFlags();

  if (!flags.PROPERTIES_ENABLED) return <FallbackEmpty />;

  return <ResolveProperty streetNumberId={streetNumberId} />;
}

function ResolveProperty({
  streetNumberId,
}: Pick<PropertySelection, "streetNumberId">) {
  const { t } = useTranslation("properties");
  const { placeId } = Route.useSearch();
  const resolution = useQuery(
    propertyOverviewQueries.resolution(streetNumberId, placeId),
  );
  const bfe = resolution.isSuccess
    ? bfeNumberSchema.safeParse(String(resolution.data.bfe))
    : undefined;

  if (resolution.isError || (bfe && !bfe.success)) {
    return (
      <div className="w-full px-4 py-8 md:px-8">
        <Notice
          color="warning"
          content={t("search.error")}
        >
          <Notice.Action onClick={() => void resolution.refetch()}>
            {t("property.error.retry")}
          </Notice.Action>
        </Notice>
        <Link
          to="/ejendomme"
          className="mt-4 inline-block underline"
        >
          {t("property.back")}
        </Link>
      </div>
    );
  }

  if (bfe?.success) {
    return (
      <Navigate
        from="/ejendomme/$streetNumberId"
        to="/ejendomme/bfe/$bfeNumber"
        params={{ bfeNumber: bfe.data }}
        search={(current) => ({
          ...current,
          accessAddressId: streetNumberId,
          placeId: resolution.data?.placeId,
        })}
        replace
      />
    );
  }

  return (
    <div
      role="status"
      className="flex w-full items-center justify-center gap-3 py-20"
    >
      <Spinner
        className="size-8"
        aria-hidden="true"
      />
      {t("search.pending")}
    </div>
  );
}
